import React from 'react';
import { Download, X } from 'lucide-react';
import { AppModal } from '../../components/AppModal';
import { AppConfirmDialog } from '../../components/AppConfirmDialog';
import { InlineLoadingState } from '../../components/LoadingSpinner';
import { DatePicker } from '../../components/DatePicker';
import { UI_Z_INDEX } from '../../utils/uiZIndex';
import { formatGermanNumber } from '../../utils/parseGermanNumber';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS } from '../../ui/controlStyles';

type Candidate = {
  invoiceId: string;
  direction: 'outgoing' | 'incoming';
  documentType: string;
  number: string;
  issueDate: string | null;
  partyName: string | null;
  gross: number;
  currency: string;
  errors: string[];
};

type Totals = Array<{ direction: 'outgoing' | 'incoming'; taxCase: string; count: number; net: number; tax: number; gross: number }>;

type Batch = {
  id: string;
  createdAt: string;
  periodFrom: string;
  periodTo: string;
  status: 'created' | 'transferred' | 'reverted';
  invoiceCount: number;
};

const TAX_CASE_LABELS: Record<string, string> = {
  vat_19: '19 %',
  vat_7: '7 %',
  vat_16: '16 %',
  vat_5: '5 %',
  reverse_charge: '§ 13b',
  intra_eu: 'Innergemeinschaftlich',
  photovoltaik_0: '0 % Photovoltaik',
  small_business: '§ 19 Kleinunternehmer',
  organschaft: 'Organschaft',
  real_estate: 'Grundstück',
  no_vat: 'Ohne Steuer',
};

const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Standard: alles ab „Übergabe ab“ bis heute; bereits exportierte Rechnungen fallen ohnehin heraus. */
function defaultRange(transferFrom: string | null): { from: string; to: string } {
  const now = new Date();
  const today = isoDay(now);
  if (transferFrom && transferFrom <= today) return { from: transferFrom, to: today };
  return { from: isoDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function money(value: number, currency = 'EUR'): string {
  return `${formatGermanNumber(value)} ${currency === 'EUR' ? '€' : currency}`;
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const d = await res.json().catch(() => null);
  if (!res.ok) throw new Error(d?.error || 'Anfrage fehlgeschlagen.');
  return d as T;
}

const STATUS_LABEL: Record<Batch['status'], string> = {
  created: 'Erzeugt',
  transferred: 'Übertragen',
  reverted: 'Zurückgenommen',
};

export function DatevExportModal({ open, onClose, onExported }: { open: boolean; onClose: () => void; onExported?: () => void }) {
  const [range, setRange] = React.useState(() => defaultRange(null));
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [candidates, setCandidates] = React.useState<Candidate[] | null>(null);
  const [totals, setTotals] = React.useState<Totals>([]);
  const [missing, setMissing] = React.useState<string[]>([]);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [batches, setBatches] = React.useState<Batch[]>([]);
  const [confirmRevert, setConfirmRevert] = React.useState<Batch | null>(null);

  const loadBatches = React.useCallback(async () => {
    try {
      const d = await api<{ batches: Batch[] }>('/api/datev/exports');
      setBatches(d.batches);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const loadPreview = React.useCallback(async (period: { from: string; to: string }) => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const d = await api<{ candidates: Candidate[]; totals: Totals; missingSettings: string[] }>(
        `/api/datev/export/preview?from=${period.from}&to=${period.to}`,
      );
      setCandidates(d.candidates);
      setTotals(d.totals);
      setMissing(d.missingSettings);
      setSelected(new Set(d.candidates.filter((c) => c.errors.length === 0).map((c) => c.invoiceId)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    void loadBatches();
    void (async () => {
      const settings = await api<{ settings: { transfer_from: string | null } }>('/api/settings/datev').catch(() => null);
      const period = defaultRange(settings?.settings.transfer_from ?? null);
      setRange(period);
      await loadPreview(period);
    })();
    // Nur beim Öffnen; der Zeitraum wird mit „Prüfen“ neu geladen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ready = candidates?.filter((c) => c.errors.length === 0) ?? [];
  const failing = candidates?.filter((c) => c.errors.length > 0) ?? [];
  const selectedCount = ready.filter((c) => selected.has(c.invoiceId)).length;

  const createExport = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const ids = ready.filter((c) => selected.has(c.invoiceId)).map((c) => c.invoiceId);
      const d = await api<{ batch: Batch }>('/api/datev/exports', {
        method: 'POST',
        body: JSON.stringify({ from: range.from, to: range.to, invoice_ids: ids }),
      });
      setNotice(`Export mit ${d.batch.invoiceCount} ${d.batch.invoiceCount === 1 ? 'Rechnung' : 'Rechnungen'} erzeugt.`);
      window.location.href = `/api/datev/exports/${d.batch.id}/download`;
      await Promise.all([loadBatches(), loadPreview(range)]);
      onExported?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const batchAction = async (batch: Batch, action: 'revert' | 'transferred') => {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/datev/exports/${batch.id}/${action}`, { method: 'POST' });
      await Promise.all([loadBatches(), loadPreview(range)]);
      onExported?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setConfirmRevert(null);
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <AppModal open={open} onClose={onClose} panelClassName="md:max-w-4xl" ariaLabelledBy="datev-export-title">
      <div className="flex min-h-0 flex-1 flex-col md:max-h-[85vh]">
        <div className="flex items-center justify-between gap-3 border-b border-app-border px-5 py-4">
          <h2 id="datev-export-title" className="text-base font-semibold text-app-text">DATEV-Export</h2>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover" aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="app-scrollbar min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="text-sm text-app-text-muted">
              Von
              <DatePicker
                value={range.from}
                onChange={(from) => setRange({ ...range, from })}
                menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                className="mt-1 w-40"
              />
            </div>
            <div className="text-sm text-app-text-muted">
              Bis
              <DatePicker
                value={range.to}
                onChange={(to) => setRange({ ...range, to })}
                menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                className="mt-1 w-40"
              />
            </div>
            <button type="button" className={APP_BTN_OUTLINE_CLASS} disabled={loading} onClick={() => void loadPreview(range)}>
              Prüfen
            </button>
          </div>

          {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
          {notice ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</div> : null}
          {missing.length > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              DATEV-Einstellungen unvollständig: {missing.join(', ')}. Bitte unter Einstellungen → Integrationen → DATEV ergänzen.
            </div>
          ) : null}

          {loading ? <InlineLoadingState label="Rechnungen werden geprüft …" /> : null}
          {!loading && candidates ? (
            candidates.length === 0 ? (
              <p className="text-sm text-app-text-muted">Keine neuen Rechnungen in diesem Zeitraum.</p>
            ) : (
              <>
                <p className="text-sm text-app-text">
                  {ready.length} bereit{failing.length > 0 ? ` · ${failing.length} mit Fehlern (werden nicht exportiert)` : ''}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-app-border text-left text-xs text-app-text-subtle">
                        <th className="w-8 py-2" />
                        <th className="py-2 pr-3 font-medium">Nummer</th>
                        <th className="py-2 pr-3 font-medium">Datum</th>
                        <th className="py-2 pr-3 font-medium">Kunde / Lieferant</th>
                        <th className="py-2 pr-3 text-right font-medium">Brutto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...failing, ...ready].map((c) => (
                        <tr key={c.invoiceId} className="border-b border-app-border/60 align-top">
                          <td className="py-2">
                            <input
                              type="checkbox"
                              aria-label={`Rechnung ${c.number} exportieren`}
                              disabled={c.errors.length > 0}
                              checked={c.errors.length === 0 && selected.has(c.invoiceId)}
                              onChange={() => toggle(c.invoiceId)}
                            />
                          </td>
                          <td className="py-2 pr-3">
                            <span className="text-app-text">{c.number || '—'}</span>
                            <span className="ml-1.5 text-xs text-app-text-subtle">{c.direction === 'outgoing' ? 'Ausgang' : 'Eingang'}</span>
                            {c.errors.length > 0 ? (
                              <ul className="mt-1 space-y-0.5 text-xs text-red-700">
                                {c.errors.map((e) => <li key={e}>{e}</li>)}
                              </ul>
                            ) : null}
                          </td>
                          <td className="py-2 pr-3 tabular-nums text-app-text-muted">{formatDate(c.issueDate)}</td>
                          <td className="py-2 pr-3 text-app-text-muted">{c.partyName || '—'}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{money(c.gross, c.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totals.length > 0 ? (
                  <div>
                    <p className="mb-2 text-sm font-medium text-app-text">Abstimmung (bereit)</p>
                    <table className="w-full max-w-xl text-sm">
                      <thead>
                        <tr className="border-b border-app-border text-left text-xs text-app-text-subtle">
                          <th className="py-1.5 pr-3 font-medium">Richtung</th>
                          <th className="py-1.5 pr-3 font-medium">Steuerfall</th>
                          <th className="py-1.5 pr-3 text-right font-medium">Anzahl</th>
                          <th className="py-1.5 pr-3 text-right font-medium">Netto</th>
                          <th className="py-1.5 pr-3 text-right font-medium">Steuer</th>
                          <th className="py-1.5 text-right font-medium">Brutto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {totals.map((t) => (
                          <tr key={`${t.direction}:${t.taxCase}`} className="border-b border-app-border/60">
                            <td className="py-1.5 pr-3">{t.direction === 'outgoing' ? 'Ausgang' : 'Eingang'}</td>
                            <td className="py-1.5 pr-3">{TAX_CASE_LABELS[t.taxCase] ?? t.taxCase}</td>
                            <td className="py-1.5 pr-3 text-right tabular-nums">{t.count}</td>
                            <td className="py-1.5 pr-3 text-right tabular-nums">{money(t.net)}</td>
                            <td className="py-1.5 pr-3 text-right tabular-nums">{money(t.tax)}</td>
                            <td className="py-1.5 text-right tabular-nums">{money(t.gross)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="mt-1 text-xs text-app-text-subtle">Fremdwährungen in Originalbetrag; DATEV rechnet mit dem hinterlegten Kurs um.</p>
                  </div>
                ) : null}
              </>
            )
          ) : null}

          {batches.length > 0 ? (
            <div>
              <p className="mb-2 text-sm font-medium text-app-text">Bisherige Exporte</p>
              <div className="divide-y divide-app-border rounded-lg border border-app-border">
                {batches.map((batch) => (
                  <div key={batch.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <span className="text-app-text">{formatDate(batch.createdAt)}</span>
                      <span className="ml-2 text-app-text-muted">
                        {formatDate(batch.periodFrom)} – {formatDate(batch.periodTo)} · {batch.invoiceCount} Rechnungen · {STATUS_LABEL[batch.status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {batch.status !== 'reverted' ? (
                        <a href={`/api/datev/exports/${batch.id}/download`} className={APP_BTN_OUTLINE_CLASS}>
                          <Download className="h-4 w-4" />
                          ZIP
                        </a>
                      ) : null}
                      {batch.status === 'created' ? (
                        <>
                          <button type="button" className={APP_BTN_OUTLINE_CLASS} disabled={busy} onClick={() => void batchAction(batch, 'transferred')}>
                            Übertragen
                          </button>
                          <button type="button" className={APP_BTN_OUTLINE_CLASS} disabled={busy} onClick={() => setConfirmRevert(batch)}>
                            Zurücknehmen
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-app-border px-5 py-3">
          <p className="text-xs text-app-text-subtle">Bitte das Ergebnis nach dem Import in DATEV prüfen (lassen).</p>
          <button type="button" className={APP_BTN_PRIMARY_CLASS} disabled={busy || loading || selectedCount === 0 || missing.length > 0} onClick={() => void createExport()}>
            Export erzeugen{selectedCount > 0 ? ` (${selectedCount})` : ''}
          </button>
        </div>
      </div>

      <AppConfirmDialog
        open={confirmRevert != null}
        onClose={() => setConfirmRevert(null)}
        title="Export zurücknehmen?"
        description="Die Rechnungen gelten danach wieder als nicht exportiert. Nur zurücknehmen, wenn die Datei noch nicht in DATEV eingelesen wurde."
        confirmLabel="Zurücknehmen"
        destructive
        isLoading={busy}
        onConfirm={() => (confirmRevert ? batchAction(confirmRevert, 'revert') : undefined)}
      />
    </AppModal>
  );
}
